import { useEffect, useEffectEvent, useState } from 'react';

import { queryOptions } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import type {
  ContentReference,
  Resource,
  SearchDocument,
} from '@cfa/pathway-fe-shared/xp';

import Constants from '@/constants';

import { queryClient } from '@/services';

import { documentAtom } from '@/atoms/document';

interface PathwayMessage {
  type?: string;
  code?: unknown;
}

export const extractBodyContent = (htmlContent?: string) => {
  if (!htmlContent) {
    return '';
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const body = doc.querySelector('body');
  return body ? body.innerHTML : '';
};

export const rewriteTridionBinaryUrl = (src: string): string => {
  const trimmed = src.trim();
  if (!trimmed) {
    return src;
  }

  const baseUrl = Constants.XP_API_IMAGE_BASE_URL;

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    const binaryPath = /\/(binary\/.*)$/.exec(trimmed)?.[1];
    return binaryPath ? `${baseUrl}/${binaryPath}` : trimmed;
  }

  if (trimmed.startsWith('/binary/')) {
    return `${baseUrl}${trimmed}`;
  }

  if (trimmed.startsWith('binary/')) {
    return `${baseUrl}/${trimmed}`;
  }

  return src;
};

const COLOR_STYLE_PROPERTIES =
  /^(color|background|background-color|border-color|border-top-color|border-right-color|border-bottom-color|border-left-color|outline-color|fill|stroke|box-shadow|text-shadow)$/i;

const BACKGROUND_WITH_URL = /url\s*\(/i;

const isColorStyleDeclaration = (declaration: string): boolean => {
  const property = declaration.split(':')[0]?.trim() ?? '';
  if (!COLOR_STYLE_PROPERTIES.test(property)) {
    return false;
  }

  // Keep `background` shorthand if it carries an image URL
  // so CMS output containing `background: url(...)` is not dropped.
  if (/^background$/i.test(property) && BACKGROUND_WITH_URL.test(declaration)) {
    return false;
  }

  return true;
};

/**
 * Drop authored colors from CMS `style` attributes so that:
 * 1. `document.css` inherit cascading parent dark-mode styles (correctness)
 * 2. inline color styles do not need overriden at every location (maintainability)
 */
export const stripInlineColorStyles = (
  htmlContent?: string,
): string | undefined => {
  if (!htmlContent) {
    return undefined;
  }

  const doc = new DOMParser().parseFromString(htmlContent, 'text/html');

  doc.querySelectorAll('[style]').forEach(element => {
    const kept = (element.getAttribute('style') ?? '')
      .split(';')
      .map(declaration => declaration.trim())
      .filter(Boolean)
      .filter(declaration => !isColorStyleDeclaration(declaration));

    if (kept.length) {
      element.setAttribute('style', kept.join('; '));
    } else {
      element.removeAttribute('style');
    }
  });

  return doc.body.innerHTML;
};

export const fixTridionImageSources = (
  htmlContent?: string,
): string | undefined => {
  if (!htmlContent) {
    return undefined;
  }
  const html = new DOMParser().parseFromString(htmlContent, 'text/html');

  html.querySelectorAll('img').forEach(image => {
    const src = image.getAttribute('src');
    if (src) {
      image.setAttribute('src', rewriteTridionBinaryUrl(src));
    }
  });

  html.querySelectorAll('iframe').forEach(iframe => {
    const src = iframe.getAttribute('src');
    if (src) {
      iframe.setAttribute('src', rewriteTridionBinaryUrl(src));
    }
  });

  return stripInlineColorStyles(html.body.innerHTML);
};

export const contentReferenceToDocument = (
  contentReference: ContentReference,
  documentId: string,
  resource?: Resource,
): SearchDocument => ({
  id: documentId,
  contentApiUrl: contentReference.contentApiUrl,
  contentUrl: contentReference.contentUrl,
  lastModified: contentReference.lastModified,
  name: contentReference.name,
  otherContentUrl: contentReference.otherContentUrl,
  otherName: contentReference.otherName,
  otherPrintUrl: contentReference.otherPrintUrl,
  printUrl: contentReference.printUrl,
  searchId: '',
  sourceSystem: contentReference.sourceSystem,
  tags: contentReference.tags,
  type: resource?.type,
});

export const useDocumentErrorListener = (): unknown => {
  const [errorMessage, setErrorMessage] = useState<unknown>();

  const onMessageReceivedFromContent = useEffectEvent(
    (eventMessage: MessageEvent) => {
      if (eventMessage.origin === Constants.PATHWAY_API_URL) {
        const data: unknown = eventMessage.data;

        // This is a message sent to indicate a successful page load
        if (data === 'ok') {
          return;
        }

        if (
          typeof data === 'object' &&
          data !== null &&
          'type' in data &&
          (data as PathwayMessage).type === 'Error'
        ) {
          setErrorMessage((data as PathwayMessage).code);
        }
      }
    },
  );

  useEffect(() => {
    window.addEventListener('message', onMessageReceivedFromContent);

    return () => {
      window.removeEventListener('message', onMessageReceivedFromContent);
    };
  }, []);

  return errorMessage;
};

export const useWindowScrollEvents = () => {
  useEffect(() => {
    window.updateScrollPosition = (top: unknown) => {
      const scrollEvent = new MessageEvent('iFrameScroll', {
        data: JSON.stringify(top),
      });
      window.dispatchEvent(scrollEvent);
    };
  }, []);
};

const SUPPORTED_LANG_CODES = Object.values(Constants.LANGUAGE);

const isSupportedLang = (lang: string) => SUPPORTED_LANG_CODES.includes(lang);

const extractLangFromUrl = (url?: string) => {
  if (!url) return Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE;

  const parts = url.split('/').filter(Boolean);
  const candidate = parts[parts.length - 2]?.slice(0, 2) ?? '';

  return isSupportedLang(candidate)
    ? candidate
    : Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE;
};

const getPreferredLanguageOptions = (
  document: SearchDocument | undefined,
): {
  primary: string;
  secondary: string;
} => {
  if (!document?.contentUrl)
    return {
      primary: Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE,
      secondary: Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE,
    };

  const primary = extractLangFromUrl(document.contentUrl);
  return {
    primary,
    secondary: document.otherContentUrl
      ? extractLangFromUrl(document.otherContentUrl)
      : primary,
  };
};

interface TridionLocalizedContent {
  url: string | undefined;
  language: string;
  title: string | undefined;
}

/** Get url for a tridion document based on using the translated url or not and send the translated. */
export const useTridionLocalizedContent = (
  document: SearchDocument,
): TridionLocalizedContent => {
  const { isTranslating } = useAtomValue(documentAtom);

  // Plan payloads can omit contentUrl. Return an empty url so Tridion can
  // show TridionError instead of throwing during render.
  if (!document.contentUrl) {
    return {
      url: undefined,
      language: Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE,
      title: document.name,
    };
  }

  const url =
    isTranslating && document.otherContentUrl
      ? document.otherContentUrl
      : document.contentUrl;
  const title =
    isTranslating && document.otherName ? document.otherName : document.name;
  const langPrefCodes = getPreferredLanguageOptions(document);
  const language =
    isTranslating && document.otherContentUrl
      ? langPrefCodes.secondary
      : langPrefCodes.primary;

  return { url, title, language };
};

export const isXpId = (str: string): boolean => str.startsWith('crn:');

/**
 * One query per document URL so every reader of a document — the Tridion view,
 * game url extraction, game size probing — shares a single request per load.
 * `gcTime: 0` drops the entry once nothing is reading it, so returning to a
 * document fetches fresh HTML instead of replaying a stale body.
 */
export const documentHtmlQueryOptions = (url: string) =>
  queryOptions({
    queryKey: ['document-html', url],
    queryFn: async () => {
      const response = await fetch(url, { credentials: 'include' });

      if (!response.ok) {
        throw new Error(`Document request failed with ${response.status}`);
      }

      return response.text();
    },
    staleTime: 0,
    gcTime: 0,
  });

export const fetchDocumentHtml = (url: string) =>
  queryClient.fetchQuery(documentHtmlQueryOptions(url));

const extractHttpsUrl = (text: string): string => {
  const trimmed = text.trim();
  if (trimmed.startsWith('https')) return trimmed;

  // HTML responses embed the game URL in an iframe src="..."; stop before
  // the closing quote (or whitespace / tag chars) so we don't keep markup.
  // One replaceAll level: `&amp;` → `&`, and double-encoded `&amp;amp;` → `&amp;`.
  const match = /https:\/\/[^"\s<>]+/.exec(trimmed);
  return match?.[0]?.replaceAll('&amp;', '&') ?? '';
};

export const extractGameUrl = async (contentUrl: string) => {
  try {
    return extractHttpsUrl(await fetchDocumentHtml(contentUrl));
  } catch {
    console.error('There was an error extracting the document URL');
  }
};

const parseIframeDimension = (
  value: string | null | undefined,
): number | undefined => {
  if (!value) {
    return;
  }

  const parsed = Number.parseInt(value, 10);

  if (!Number.isFinite(parsed) || parsed <= 0) {
    return;
  }

  return parsed;
};

/**
 * Extract the Tridion VIDEO/GAME description as authored HTML.
 * Prefers a non-empty direct-child `.section` that follows the iframe
 * (content abstract), then `.shortdesc` (game/legacy). Other siblings are
 * left for a follow-up (PATHWAY-3778).
 */
export const extractTridionMediaDescriptionHtml = (
  htmlContent?: string,
): string | undefined => {
  if (!htmlContent) {
    return;
  }

  const doc = new DOMParser().parseFromString(htmlContent, 'text/html');
  const iframe = doc.querySelector('iframe');
  const parent = iframe?.parentElement;
  if (!iframe || !parent) {
    return;
  }

  const hasText = (el: Element) => Boolean(el.textContent.trim());
  const followsIframe = (el: Element) =>
    Boolean(
      iframe.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING,
    );

  const sectionAfterIframe =
    Array.from(parent.querySelectorAll(':scope > .section')).find(
      el => hasText(el) && followsIframe(el),
    ) ??
    // Abstract may sit beside an embed wrapper rather than next to the iframe.
    (parent.parentElement
      ? Array.from(
          parent.parentElement.querySelectorAll(':scope > .section'),
        ).find(el => hasText(el) && followsIframe(el))
      : undefined);

  const shortDesc =
    Array.from(parent.querySelectorAll(':scope > .shortdesc')).find(
      el => hasText(el) && followsIframe(el),
    ) ??
    Array.from(parent.querySelectorAll(':scope > .shortdesc')).find(hasText) ??
    // Legacy games often place .shortdesc outside the iframe wrapper.
    Array.from(doc.querySelectorAll('.shortdesc')).find(hasText);

  const description = sectionAfterIframe ?? shortDesc;
  if (!description) {
    return;
  }

  return description.outerHTML;
};

/** Extract game information from HTML content. */
export const extractTridionGameInformation = (htmlContent?: string) => {
  if (!htmlContent) {
    return;
  }
  const html = new DOMParser().parseFromString(htmlContent, 'text/html');
  const iframe = html.querySelector('iframe');
  const iframeStyle = iframe?.getAttribute('style') ?? '';
  const styleWidthMatch = /width:\s*(\d+)px/i.exec(iframeStyle);
  const styleHeightMatch = /height:\s*(\d+)px/i.exec(iframeStyle);

  return {
    descriptionHtml: extractTridionMediaDescriptionHtml(htmlContent),
    url: iframe?.src,
    width:
      parseIframeDimension(iframe?.getAttribute('width')) ??
      parseIframeDimension(styleWidthMatch?.[1]),
    height:
      parseIframeDimension(iframe?.getAttribute('height')) ??
      parseIframeDimension(styleHeightMatch?.[1]),
  };
};
